import React, { useState, useEffect } from "react";
import { Container, Row, Col, Image, Button, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { collection, addDoc, getDoc, doc, getDocs, updateDoc, increment } from 'firebase/firestore';
import { db, auth } from "../firebase";
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';

function linkify(text){
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.split(urlRegex).map((part, index) => {
    if (part.match(urlRegex)) {
      try {
        const parsedUrl = new URL(part);
        if (!["http:", "https:"].includes(parsedUrl.protocol)) {
          return part;
        }
      } catch (error) {
        return part;
      }

      return (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return part;
  });
}

function BlogPage({ isAuth }) {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [blog, setBlog] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [viewCount, setViewCount] = useState(0);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    const fetchBlogAndIncrementViews = async () => {
      try {
        const blogRef = doc(db, "blog", blogId);
        // Increment the views field atomically
        await updateDoc(blogRef, { views: increment(1) });
        const blogDoc = await getDoc(blogRef);
        if (blogDoc.exists()) {
          setBlog(blogDoc.data());
          setViewCount(blogDoc.data().views || 1);
        }
      } catch (e) {
        console.error("Error fetching/incrementing blog views: ", e);
      }
    };

    const fetchComments = async () => {
      try {
        const commentsRef = collection(doc(db, "blog", blogId), "comments");
        const commentsSnapshot = await getDocs(commentsRef);
        const commentsList = commentsSnapshot.docs.map(doc => doc.data());
        setComments(commentsList);
      } catch (e) {
        console.error("Error fetching comments: ", e);
      }
    };

    fetchBlogAndIncrementViews();
    fetchComments();
  }, [blogId]);

  useEffect(() => {
    const updateReadingProgress = () => {
      const docEl = document.documentElement;
      const totalHeight = docEl.scrollHeight - docEl.clientHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setReadingProgress(Math.min(100, Math.max(0, progress)));
    };

    updateReadingProgress();
    window.addEventListener('scroll', updateReadingProgress);
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);

  const handleCommentClick = async (event) => {
    event.preventDefault();
    if (isAuth) {
      try {
        const blogRef = doc(db, "blog", blogId);
        await addDoc(collection(blogRef, "comments"), {
          comment,
          commenter: { name: auth.currentUser.displayName },
        });
        toast.success('Successfully posted');
        setComment("");

        const commentsRef = collection(blogRef, "comments");
        const commentsSnapshot = await getDocs(commentsRef);
        const commentsList = commentsSnapshot.docs.map(doc => doc.data());
        setComments(commentsList);
      } catch (e) {
        console.error("Error adding document: ", e);
        toast.error("Failed to post comment");
      }
    } else {
      toast.error("You are not logged in");
      navigate('/login');
    }
  };

  if (!blog) {
    return <div>Loading...</div>;
  }

  const currentContent = language === 'np' && blog.hastranslation
    ? (blog.postNp || "")
    : (blog.postEn || blog.post || "");

  const readingMinutes = Math.max(1, Math.ceil(currentContent.split(/\s+/).length / 200));

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div>
      <div className="reading-progress" aria-hidden="true">
        <div className="reading-progress__bar" style={{ width: `${readingProgress}%` }} />
      </div>
      <Container>
        <Row>
          <Col sm={5}>
            <h1 className="blog-title"><strong>{blog.title}</strong></h1>
          </Col>
          <Col sm={7}>
            <Image
              src={blog.imageUrl}
              thumbnail
              style={{ height: "350px", width: "auto" }}
            />
          </Col>
        </Row>
      </Container>
      <Container>
        <Row>
          <Col sm={8}>
            <p className="author-date"><strong>{blog.author.name}</strong><strong style={{ marginLeft: '12px' }}>{blog.Date}</strong></p>
            <div className="blog-meta">
              <span>{readingMinutes} min read</span>
              <span style={{ marginLeft: '16px' }}>👁️ {viewCount} views</span>
              <Button
                variant="outline-light"
                size="sm"
                className="copy-link-button"
                onClick={handleCopyLink}
                style={{ marginLeft: '16px' }}
              >
                Copy link
              </Button>
              {blog.hastranslation && (
                <span style={{ marginLeft: '16px' }}>
                  <Button
                    variant={language === 'en' ? 'secondary' : 'black'}
                    size="sm"
                    onClick={() => setLanguage('en')}
                    style={{ marginRight: '4px' }}
                  >
                    EN
                  </Button>
                  <Button
                    variant={language === 'np' ? 'secondary' : 'black'}
                    size="sm"
                    onClick={() => setLanguage('np')}
                    style={{ fontFamily: "'Noto Sans Devanagari', sans-serif" }}
                  >
                    नेपाली
                  </Button>
                </span>
              )}
            </div>
            <div
              className="blog-post"
              style={language === 'np' ? { fontFamily: "'Noto Sans Devanagari', 'Arial Unicode MS', sans-serif" } : {}}
            >
              <ReactMarkdown>{currentContent}</ReactMarkdown>
            </div>
          </Col>
          <Col sm={4}>
          </Col>
        </Row>
        <Container>
          <Col sm={6}>
            <div className="comment-section">
              <h4>Comment Section</h4>
              <Form>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  placeholder="Write your comments here"  
                  value={comment} 
                  onChange={(event) => setComment(event.target.value)}
                />
                <Button 
                  variant="success" 
                  size="sm" 
                  style={{ marginTop: '10px' }} 
                  onClick={handleCommentClick}
                >
                  Submit
                </Button>
              </Form>
              <div>
                {comments.map((comment, index) => (
                  <div key={index} className="comment">
                    <p><strong>{comment.commenter.name}</strong>:{linkify(comment.comment)}</p>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Container>
      </Container>
    </div>
  );
}

export default BlogPage;
